import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import CloseIcon from "@mui/icons-material/Close";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import SearchIcon from "@mui/icons-material/Search";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import Menu from "@mui/material/Menu";
import { styled, SxProps } from "@mui/material/styles";
import TextField from "@mui/material/TextField";
import { RootState, useAttorneyHubDispatch } from "app/realtime-store/redux-store";
import PaginationParameters from "common/contracts/pagination-parameters";
import useDebounce from "common/hooks/use-debounce";
import PermissionFilterParameters from "permissions/entities/permission/api/request-contracts/permission-filter-parameters";
import PermissionTemplateQuery from "permissions/entities/permission/api/request-contracts/permission-template-query";
import PermissionTemplate from "permissions/entities/permission/permission-template";
import { getErrorLoadingPermissionTemplatesByQuery, getIsLoadingPermissionTemplatesByQuery, populatePermissionTemplatesByQuery, updatePermissionTemplateInterfaceQuery } from "permissions/entities/permission/store/permission-templates-redux-slice";
import React, { ReactNode, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useSession } from "users/session/session-context";

const TemplatesMenu = styled(Menu)(({ theme }) => ({
  '& .MuiPaper-root': {
    border: `1px solid ${theme.palette.divider}`,
    marginTop: theme.spacing(0.5)
  }
}));
const MenuContainer = styled('div')(({ theme }) => ({
  minWidth: '300px',
  padding: theme.spacing(0.75, 1.5)
}));
const Header = styled('div')(() => ({
  alignItems: 'center',
  display: 'grid',
  gap: 1,
  gridTemplateColumns: '1fr auto'
}));
const MenuContentContainer = styled('div')(({ theme }) => ({
  marginTop: theme.spacing(1),
  maxHeight: '500px',
  maxWidth: '400px',
  overflowY: 'auto'
}));

export type ApplyTemplateMenuButtonProps = {
  className?: string;
  disabled?: boolean;
  sx?: SxProps;
  onApply?: (template: PermissionTemplate) => void;
};

export default function ApplyTemplateMenuButton(props: Readonly<ApplyTemplateMenuButtonProps>): ReactNode {
  const {
    className,
    disabled,
    sx,
    onApply
  } = props;

  const interfaceName = "apply-template-menu";
  const permissionTemplatesQuery = useSelector(
    (state: RootState) =>
      state.permissionTemplates.interfaceQueries.entries[interfaceName]
  );
  const permissionTemplatesResponse = useSelector(
    (state: RootState) =>
      permissionTemplatesQuery ? state.permissionTemplates.byQuery.entries[permissionTemplatesQuery.cacheKey()] : undefined
  );
  const isLoadingPermissionTemplates = getIsLoadingPermissionTemplatesByQuery(permissionTemplatesQuery);
  const permissionTemplatesLoadErrors = getErrorLoadingPermissionTemplatesByQuery(permissionTemplatesQuery);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [search, setSearch] = useState<string>('');

  const session = useSession();
  const dispatch = useAttorneyHubDispatch();
  const debouncedSearch = useDebounce<string>(search, 300);

  const templates = useMemo(() => {
    const all = permissionTemplatesResponse?.data ?? [];
    if (!debouncedSearch.trim()) return all;
    const q = debouncedSearch.toLowerCase();
    return all.filter(t => (t?.name ?? "").toLowerCase().includes(q));
  }, [permissionTemplatesResponse, debouncedSearch]);

  const open = Boolean(anchorEl);

  useEffect(() => {
    dispatch(
      updatePermissionTemplateInterfaceQuery({
        interfaceName,
        query: new PermissionTemplateQuery(
          new PermissionFilterParameters(),
          undefined,
          new PaginationParameters(0, 25)
        )
      })
    );

    const newTemplatesQuery = new PermissionTemplateQuery(
      Object.assign(new PermissionFilterParameters(), { name: debouncedSearch.trim() }),
      undefined,
      new PaginationParameters(0, 25)
    );

    if (newTemplatesQuery.isEqualTo(permissionTemplatesQuery)) return;

    dispatch(
      populatePermissionTemplatesByQuery({
        session,
        query: newTemplatesQuery
      })
    );
  }, [debouncedSearch, dispatch]);

  function handleMenuOpen(event: React.MouseEvent<HTMLElement>) {
    setAnchorEl(event.currentTarget);
  }

  function handleClose() {
    setAnchorEl(null);
  }

  function handleApplyTemplate(template: PermissionTemplate) {
    onApply?.(template);
    handleClose();
  }

  return (
    <Box className={className} sx={sx}>
      <Button
        startIcon={<AutoFixHighIcon />}
        endIcon={<KeyboardArrowDownIcon />}
        variant="contained"
        disabled={disabled || open}
        color="secondary"
        disableElevation
        onClick={handleMenuOpen}
      >
        Apply Template
      </Button>
      <TemplatesMenu
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        elevation={0}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        marginThreshold={0}
      >
        <MenuContainer>
          <Header>
            <TextField
              placeholder="Search templates"
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                  endAdornment: search ? (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setSearch('')}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ) : null
                }
              }}
              size="small"
              fullWidth
              variant="standard"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </Header>
          <MenuContentContainer>
            <List disablePadding dense>
              {isLoadingPermissionTemplates && (
                <ListItem disableGutters>
                  <ListItemText primary="Loading templates..." />
                </ListItem>
              )}
              {!isLoadingPermissionTemplates && templates.length === 0 && (
                <ListItem disableGutters>
                  <ListItemText primary="No templates available" />
                </ListItem>
              )}
              {permissionTemplatesLoadErrors && (
                <ListItem disableGutters>
                  <ListItemText primary="Error loading templates" />
                </ListItem>
              )}
              {!isLoadingPermissionTemplates && templates.map((template) => (
                <ListItem
                  key={template.id.value}
                  disableGutters
                  sx={{ pr: 10 }}
                  secondaryAction={
                    <Button
                      size="small"
                      onClick={() => handleApplyTemplate(template)}
                    >
                      Apply
                    </Button>
                  }>
                  <ListItemText
                    primary={template.name}
                    secondary={template.description}
                  />
                </ListItem>
              ))}
            </List>
          </MenuContentContainer>
        </MenuContainer>
      </TemplatesMenu>
    </Box>
  );
}
